import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Database, Lock, Search, RefreshCw, Download, Trash2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import PageBanner from './PageBanner';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';

const getAffiliationColor = (affiliation) => {
  switch (affiliation) {
    case 'Preparatory student': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'Engineering': return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'Bachelor-License': return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'Masters': return 'bg-teal-100 text-teal-800 border-teal-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

// Custom Confirm Modal matching the website's sharp theme
function ConfirmModal({ isOpen, name, onConfirm, onCancel }) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onCancel}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 10 }}
            transition={{ type: 'spring', damping: 28, stiffness: 320 }}
            className="bg-white w-full max-w-sm relative shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* L corners */}
            <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-red-500 pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-red-500 pointer-events-none" />

            <div className="p-8 text-center">
              <div className="w-14 h-14 bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-5">
                <AlertTriangle className="w-7 h-7 text-red-500" />
              </div>
              <h3 className="text-lg font-black text-[#00334d] uppercase tracking-tight mb-2">
                Delete Registration
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to remove <span className="font-bold text-[#00334d]">{name}</span> from the database? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={onCancel}
                  className="flex-1 bg-[#f4f6f9] hover:bg-gray-200 text-[#00334d] font-bold text-xs uppercase tracking-widest py-3 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={onConfirm}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold text-xs uppercase tracking-widest py-3 transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null, name: '' });
  
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [affiliationFilter, setAffiliationFilter] = useState('All');

  // Check login status on mount
  useEffect(() => {
    if (localStorage.getItem('ieee_admin_auth') === 'true') {
      setIsAuthenticated(true);
      fetchRegistrations();
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === 'admin123') {
      setIsAuthenticated(true);
      localStorage.setItem('ieee_admin_auth', 'true');
      fetchRegistrations();
    } else {
      toast.error('Incorrect password');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('ieee_admin_auth');
    setPassword('');
    setRegistrations([]);
  };

  const fetchRegistrations = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('registrations')
      .select('*')
      .order('created_at', { ascending: true }); // Oldest first (1 is the first registered)

    if (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } else {
      setRegistrations(data || []);
    }
    setLoading(false);
  };

  const handleDelete = (id, name) => {
    setConfirmDialog({ open: true, id, name });
  };

  const confirmDelete = async () => {
    const { id } = confirmDialog;
    setConfirmDialog({ open: false, id: null, name: '' });
    const { error } = await supabase.from('registrations').delete().eq('id', id);
    if (error) {
      toast.error(`Failed to delete: ${error.message}`);
    } else {
      toast.success("Registration deleted");
      setRegistrations(prev => prev.filter(r => r.id !== id));
    }
  };

  const togglePaidStatus = async (id, currentStatus) => {
    const newStatus = !currentStatus;
    const { data, error } = await supabase
      .from('registrations')
      .update({ is_paid: newStatus })
      .eq('id', id)
      .select();
      
    if (error) {
      console.error('Supabase update error:', error);
      toast.error(`DB Error: ${error.message}`, { duration: 6000 });
    } else {
      console.log('Update successful:', data);
      toast.success(newStatus ? "Marked as Paid!" : "Marked as Unpaid");
      setRegistrations(prev => prev.map(r => r.id === id ? { ...r, is_paid: newStatus } : r));
    }
  };

  const filteredRegistrations = registrations.filter(r => {
    const matchesSearch = r.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) || r.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAffiliation = affiliationFilter === 'All' || r.affiliation === affiliationFilter;
    return matchesSearch && matchesAffiliation;
  });

  const exportToCSV = () => {
    if (registrations.length === 0) return;
    const headers = ['No.', 'Name', 'Email', 'Phone', 'Affiliation', 'Source', 'Comments', 'Paid', 'Date'];
    const csvContent = [
      headers.join(','),
      ...registrations.map((r, i) => [
        `"${i + 1}"`,
        `"${r.full_name || ''}"`,
        `"${r.email || ''}"`,
        `"${r.phone || ''}"`,
        `"${r.affiliation || ''}"`,
        `"${r.source || ''}"`,
        `"${r.comments?.replace(/\n/g, ' ') || ''}"`,
        `"${r.is_paid ? 'Yes' : 'No'}"`,
        `"${new Date(r.created_at).toLocaleDateString()}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'ieee_registrations.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#fafcff] flex items-center justify-center p-4">
        <div className="bg-white p-8 border border-[#006699]/20 shadow-xl max-w-md w-full relative">
          <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[#006699]" />
          <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-[#006699]" />
          
          <div className="w-16 h-16 bg-[#006699]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-[#006699]" />
          </div>
          <h2 className="text-2xl font-black text-center text-[#00334d] uppercase tracking-widest mb-6">Admin Access</h2>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#f4f6f9] border border-transparent focus:border-[#006699] outline-none px-4 py-3 text-sm"
            />
            <button
              type="submit"
              className="w-full bg-[#006699] text-white font-bold text-xs uppercase tracking-widest py-3 hover:bg-[#00334d] transition-colors"
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafcff]">
      <ConfirmModal
        isOpen={confirmDialog.open}
        name={confirmDialog.name}
        onConfirm={confirmDelete}
        onCancel={() => setConfirmDialog({ open: false, id: null, name: '' })}
      />
      <PageBanner 
        title="Admin Dashboard" 
        description="View and manage IEEE ISIMS SB membership registrations." 
        Icon={Database} 
      />

      <div className="max-w-[90rem] mx-auto px-4 sm:px-6 py-12">
        
        {/* Top Header: Total Count + Logout */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-4">
          <div>
            <h2 className="text-3xl font-black text-[#00334d] tracking-tighter uppercase">Members</h2>
            <p className="text-[#006699] font-bold text-sm tracking-widest uppercase mt-1">Total Registrations: {registrations.length}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 bg-white border border-red-200 text-red-500 hover:bg-red-50 hover:border-red-400 px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors shadow-sm self-start sm:self-auto"
          >
            <Lock className="w-3.5 h-3.5" />
            Logout
          </button>
        </div>

        {/* Controls */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
            {/* Search */}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white border border-[#006699]/20 focus:border-[#006699] outline-none pl-10 pr-4 py-2 text-sm shadow-sm"
              />
            </div>
            
            {/* Affiliation Filter */}
            <select
              value={affiliationFilter}
              onChange={(e) => setAffiliationFilter(e.target.value)}
              className="w-full sm:w-auto bg-white border border-[#006699]/20 focus:border-[#006699] outline-none px-4 py-2 text-sm shadow-sm cursor-pointer"
            >
              <option value="All">All Affiliations</option>
              <option value="Preparatory student">Preparatory student</option>
              <option value="Engineering">Engineering</option>
              <option value="Bachelor-License">Bachelor-License</option>
              <option value="Masters">Masters</option>
            </select>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={fetchRegistrations}
              className="bg-white border border-[#006699]/20 hover:border-[#006699] text-[#006699] px-4 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-2 transition-colors shadow-sm"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={exportToCSV}
              className="bg-[#006699] hover:bg-[#00334d] text-white px-4 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-2 transition-colors shadow-sm"
            >
              <Download className="w-3.5 h-3.5" />
              Export
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#006699]/20 shadow-lg overflow-x-auto">
          <table className="w-full text-left text-sm whitespace-nowrap">
            <thead className="bg-[#f4f6f9] border-b border-[#006699]/10">
              <tr>
                <th className="px-4 py-4 font-bold text-[#00334d] uppercase tracking-wider text-xs">#</th>
                <th className="px-4 py-4 font-bold text-[#00334d] uppercase tracking-wider text-xs">Name</th>
                <th className="px-4 py-4 font-bold text-[#00334d] uppercase tracking-wider text-xs">Email</th>
                <th className="px-4 py-4 font-bold text-[#00334d] uppercase tracking-wider text-xs">Phone</th>
                <th className="px-4 py-4 font-bold text-[#00334d] uppercase tracking-wider text-xs">Affiliation</th>
                <th className="px-4 py-4 font-bold text-[#00334d] uppercase tracking-wider text-xs">Source</th>
                <th className="px-4 py-4 font-bold text-[#00334d] uppercase tracking-wider text-xs">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#006699]/5">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    Loading registrations...
                  </td>
                </tr>
              ) : filteredRegistrations.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    No registrations found.
                  </td>
                </tr>
              ) : (
                filteredRegistrations.map((reg, index) => (
                  <tr key={reg.id} className="hover:bg-[#006699]/5 transition-colors group">
                    <td className="px-4 py-4 font-black text-[#006699]">{index + 1}</td>
                    <td className="px-4 py-4 font-medium text-gray-900">{reg.full_name}</td>
                    <td className="px-4 py-4 text-gray-600">{reg.email}</td>
                    <td className="px-4 py-4 text-gray-600">{reg.phone}</td>
                    <td className="px-4 py-4">
                      <span className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-none border ${getAffiliationColor(reg.affiliation)}`}>
                        {reg.affiliation}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-gray-600 truncate max-w-[150px]">{reg.source || '-'}</td>
                    
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        {/* Paid Toggle Button */}
                        <button
                          onClick={() => togglePaidStatus(reg.id, reg.is_paid)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors border ${
                            reg.is_paid 
                              ? 'bg-green-100 text-green-700 border-green-200 hover:bg-green-200' 
                              : 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
                          }`}
                        >
                          {reg.is_paid ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                          {reg.is_paid ? 'Paid' : 'Unpaid'}
                        </button>

                        {/* Delete Button */}
                        <button
                          onClick={() => handleDelete(reg.id, reg.full_name)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 transition-colors"
                          title="Delete Registration"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
